import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../../renderer/TextsProvider.js';
/**
 * The overlay an upsell offer plays in — shared since feature 0069 lot C by the POST-purchase chain (on the
 * confirmation) and the CART chain (on add-to-cart).
 *
 * Deliberately small and self-contained. Escape and a tap on the backdrop both close it, because a buyer who
 * is being offered something extra owes it nothing: on the confirmation their order is already placed, and in
 * the cart journey their product is one click from the basket. A buyer who feels trapped closes the tab, and
 * that costs the order the shop already had.
 *
 * `busy` stops clicks WITHOUT fading the panel: fading it to 60% made the screen look frozen, and a buyer who
 * thinks a screen is frozen taps again or leaves — on a charge that is going through (founder, 2026-08-12).
 * The button carries the feedback instead.
 */
export function UpsellModal({ busy, onClose, children, }) {
    const t = useT();
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        // The page behind must not scroll under the sheet on mobile.
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [onClose]);
    if (typeof document === 'undefined')
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("style", { children: UPSELL_MODAL_CSS }), _jsx("div", { className: "ppu-ov", role: "dialog", "aria-modal": "true", "aria-label": t('a11y.special_offer'), onClick: (e) => {
                    if (e.target === e.currentTarget)
                        onClose();
                }, children: _jsxs("div", { className: busy ? 'ppu-panel is-busy' : 'ppu-panel', children: [_jsx("button", { type: "button", className: "ppu-close", onClick: onClose, "aria-label": t('a11y.close'), children: "\u00D7" }), children] }) })] }), document.body);
}
/**
 * The modal's OWN chrome — it travels with the component, and that is the whole point.
 *
 * These rules used to live in `PostPurchaseUpsellChain`'s stylesheet, which was fine while that chain was
 * the only caller. Lot C gave the modal a second caller (the CART chain on add-to-cart), on a page where
 * the post-purchase chain is never mounted: `.ppu-ov` was then an undefined class, so the overlay lost
 * `position:fixed` and rendered as an ordinary block at the BOTTOM of the product page — measured at
 * 5874px down, below everything. The chain played, the offer was in the DOM, and no buyer could ever see
 * it (founder, 2026-09-22: « j'ai pas reçu de upsells »).
 *
 * A component that paints itself cannot be broken by where it is mounted. Static only — not one
 * seller-supplied value belongs in a stylesheet; the offer's own look comes from the card's React styles.
 */
const UPSELL_MODAL_CSS = `
.ppu-ov {
  position:fixed; inset:0; z-index:2147483000; display:flex; align-items:center; justify-content:center;
  padding:22px; background:rgba(15,12,25,.5); animation:ppuFade .2s ease;
}
.ppu-panel {
  position:relative; inline-size:min(440px,100%); max-block-size:88vh; overflow-y:auto;
  background:#ffffff; border-radius:20px; padding:30px 22px 22px;
  box-shadow:0 30px 80px -24px rgba(15,12,25,.5), 0 8px 22px -12px rgba(15,12,25,.28);
  animation:ppuRise .24s cubic-bezier(.2,.9,.3,1); transition:opacity .15s ease;
}
/* Busy = the offer is being added. The panel stays FULLY VISIBLE and only stops accepting clicks: fading
   the whole card to 60% made the screen look frozen, and a buyer who thinks a screen is frozen taps again
   or closes the tab — on an order that is going through (founder, 2026-08-12). The button says what is
   happening; that is where the feedback belongs. */
.ppu-panel.is-busy { pointer-events:none; }
.ppu-close {
  position:absolute; inset-block-start:12px; inset-inline-end:12px; inline-size:34px; block-size:34px;
  display:flex; align-items:center; justify-content:center; border:0; border-radius:50%;
  background:rgba(0,0,0,.06); font-size:21px; line-height:1; cursor:pointer; color:#6b7280;
}
.ppu-close:hover { background:rgba(0,0,0,.12); }
@keyframes ppuFade { from { opacity:0 } to { opacity:1 } }
@keyframes ppuRise { from { opacity:0; transform:translateY(10px) scale(.97) } to { opacity:1; transform:none } }
@media (max-width:749px) {
  .ppu-ov { align-items:flex-end; padding:0; }
  .ppu-panel {
    inline-size:100%; max-inline-size:100%; max-block-size:92vh; border-radius:22px 22px 0 0;
    padding-block-end:max(22px, env(safe-area-inset-bottom)); animation:ppuSheet .28s cubic-bezier(.2,.9,.3,1);
  }
}
@keyframes ppuSheet { from { transform:translateY(100%) } to { transform:none } }
@media (prefers-reduced-motion:reduce) { .ppu-ov, .ppu-panel { animation:none } }
@media (prefers-color-scheme: dark) {
  .ppu-panel { background:#17171b; }
  .ppu-close { background:rgba(255,255,255,.1); color:#9aa0a6; }
}
`;
//# sourceMappingURL=upsellModal.js.map